<template>
  <v-container fluid>
    <v-row v-if="loading" class="fill-height" align="center" justify="center">
      <v-col cols="auto">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </v-col>
    </v-row>

    <template v-else-if="committee">
      <!-- Header -->
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="d-flex align-center mb-4">
            <v-btn
              icon="mdi-arrow-left"
              variant="text"
              @click="$router.push('/committees')"
              class="mr-4"
            ></v-btn>
            <div class="flex-grow-1">
              <h1 class="text-h4 font-weight-bold">{{ committee.name }}</h1>
              <p class="text-body-2 text-medium-emphasis">{{ committee.description }}</p>
            </div>
            <v-chip
              :color="committee.is_active ? 'success' : 'error'"
              variant="flat"
              class="mr-4"
            >
              {{ committee.is_active ? 'Active' : 'Inactive' }}
            </v-chip>
            <v-btn
              color="primary"
              prepend-icon="mdi-pencil"
              variant="outlined"
              @click="editCommittee"
            >
              Edit Committee
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Committee Info Cards -->
      <v-row class="mb-4">
        <v-col cols="12" md="3">
          <v-card>
            <v-card-text>
              <div class="d-flex align-center">
                <v-avatar color="primary" class="mr-4">
                  <v-icon>mdi-account-multiple</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h4 font-weight-bold">{{ members.length }}</div>
                  <div class="text-caption text-medium-emphasis">Members</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card>
            <v-card-text>
              <div class="d-flex align-center">
                <v-avatar color="success" class="mr-4">
                  <v-icon>mdi-calendar-check</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h4 font-weight-bold">0</div>
                  <div class="text-caption text-medium-emphasis">Meetings</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card>
            <v-card-text>
              <div class="d-flex align-center">
                <v-avatar color="info" class="mr-4">
                  <v-icon>mdi-file-document</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h4 font-weight-bold">0</div>
                  <div class="text-caption text-medium-emphasis">Documents</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card>
            <v-card-text>
              <div class="d-flex align-center">
                <v-avatar color="warning" class="mr-4">
                  <v-icon>mdi-gavel</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h4 font-weight-bold">0</div>
                  <div class="text-caption text-medium-emphasis">Resolutions</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Committee Chair -->
      <v-row class="mb-4" v-if="committee.chair">
        <v-col cols="12">
          <v-card>
            <v-card-title class="d-flex align-center">
              <v-icon class="mr-2">mdi-star</v-icon>
              Committee Chair
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center">
                <v-avatar color="primary" size="64" class="mr-4">
                  <v-icon size="32">mdi-account</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h6 font-weight-medium">{{ committee.chair.full_name }}</div>
                  <div class="text-body-2 text-medium-emphasis">{{ committee.chair.email }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Committee Members -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title class="d-flex align-center">
              <v-icon class="mr-2">mdi-account-group</v-icon>
              Committee Members
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                prepend-icon="mdi-plus"
                size="small"
                @click="openAddMemberDialog"
              >
                Add Member
              </v-btn>
            </v-card-title>

            <v-data-table
              :headers="memberHeaders"
              :items="members"
              :loading="loadingMembers"
              class="elevation-0"
            >
              <!-- Member Name Column -->
              <template v-slot:item.name="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar color="primary" size="40" class="mr-3">
                    <v-icon color="white">mdi-account</v-icon>
                  </v-avatar>
                  <div>
                    <div class="font-weight-medium">{{ item.name }}</div>
                    <div class="text-caption text-medium-emphasis">{{ item.email }}</div>
                  </div>
                </div>
              </template>

              <!-- Role Column -->
              <template v-slot:item.role="{ item }">
                <v-chip color="info" size="small">
                  {{ item.role }}
                </v-chip>
              </template>

              <!-- Joined Date Column -->
              <template v-slot:item.joined_at="{ item }">
                {{ formatDate(item.joined_at) }}
              </template>

              <!-- Actions Column -->
              <template v-slot:item.actions="{ item }">
                <v-btn
                  icon="mdi-pencil"
                  size="small"
                  variant="text"
                  @click="editMemberRole(item)"
                  class="mr-1"
                ></v-btn>
                <v-btn
                  icon="mdi-delete"
                  size="small"
                  variant="text"
                  color="error"
                  @click="removeMember(item)"
                ></v-btn>
              </template>

              <!-- No data -->
              <template v-slot:no-data>
                <div class="text-center py-8">
                  <v-icon size="64" color="grey-lighten-2" class="mb-4">mdi-account-group-outline</v-icon>
                  <p class="text-h6 text-medium-emphasis">No members assigned yet</p>
                  <v-btn
                    color="primary"
                    variant="outlined"
                    prepend-icon="mdi-plus"
                    @click="openAddMemberDialog"
                    class="mt-4"
                  >
                    Add First Member
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Add Member Dialog -->
    <v-dialog v-model="addMemberDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5 pa-6 bg-primary">
          <v-icon class="mr-2">mdi-plus</v-icon>
          Add Committee Member
        </v-card-title>

        <v-card-text class="pa-6">
          <v-form ref="memberFormRef" v-model="memberFormValid">
            <v-select
              v-model="memberForm.member_id"
              :items="availableBoardMembers"
              item-title="full_name"
              item-value="id"
              label="Board Member"
              :rules="[rules.required]"
              variant="outlined"
              prepend-inner-icon="mdi-account"
              class="mb-4"
            ></v-select>

            <v-select
              v-model="memberForm.role"
              :items="roleOptions"
              label="Role"
              :rules="[rules.required]"
              variant="outlined"
              prepend-inner-icon="mdi-shield-account"
            ></v-select>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="addMemberDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="savingMember"
            :disabled="!memberFormValid"
            @click="addMember"
          >
            Add Member
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Committee Dialog -->
    <v-dialog v-model="editDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5 pa-6 bg-primary">
          <v-icon class="mr-2">mdi-pencil</v-icon>
          Edit Committee
        </v-card-title>

        <v-card-text class="pa-6">
          <v-form ref="editFormRef" v-model="editFormValid">
            <v-text-field
              v-model="editForm.name"
              label="Committee Name"
              :rules="[rules.required]"
              variant="outlined"
              prepend-inner-icon="mdi-account-group"
              class="mb-4"
            ></v-text-field>

            <v-textarea
              v-model="editForm.description"
              label="Description"
              variant="outlined"
              prepend-inner-icon="mdi-text"
              rows="3"
              class="mb-4"
            ></v-textarea>

            <v-select
              v-model="editForm.chair_id"
              :items="boardMembers"
              item-title="full_name"
              item-value="id"
              label="Committee Chair (Optional)"
              variant="outlined"
              prepend-inner-icon="mdi-star"
              clearable
              class="mb-4"
            ></v-select>

            <v-switch
              v-model="editForm.is_active"
              label="Active"
              color="primary"
              hide-details
            ></v-switch>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="closeEditDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="savingEdit"
            :disabled="!editFormValid"
            @click="saveCommittee"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { useToast } from 'vue-toastification'
import { useBreadcrumb } from '@/composables/useBreadcrumb'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const { setBreadcrumbTitle } = useBreadcrumb()

// Data
const committee = ref(null)
const members = ref([])
const boardMembers = ref([])
const loading = ref(false)
const loadingMembers = ref(false)
const addMemberDialog = ref(false)
const editDialog = ref(false)
const memberFormValid = ref(false)
const editFormValid = ref(false)
const savingMember = ref(false)
const savingEdit = ref(false)
const memberFormRef = ref(null)
const editFormRef = ref(null)

const memberForm = ref({
  member_id: null,
  role: 'Member'
})

const editForm = ref({
  name: '',
  description: '',
  chair_id: null,
  is_active: true
})

// Table headers
const memberHeaders = [
  { title: 'Member', key: 'name', sortable: true },
  { title: 'Role', key: 'role', sortable: true },
  { title: 'Joined Date', key: 'joined_at', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center' }
]

// Role options
const roleOptions = ['Chair', 'Vice Chair', 'Secretary', 'Treasurer', 'Member']

// Validation rules
const rules = {
  required: value => !!value || 'This field is required'
}

// Computed
const availableBoardMembers = computed(() => {
  const memberIds = new Set(members.value.map(m => m.member_id))
  return boardMembers.value.filter(m => !memberIds.has(m.id))
})

// Methods
const fetchCommittee = async () => {
  try {
    loading.value = true
    const response = await api.get(`/committees/${route.params.id}`)
    committee.value = response.data.committee
    setBreadcrumbTitle(committee.value.name)
  } catch (error) {
    console.error('Error fetching committee:', error)
    toast.error('Failed to load committee details')
  } finally {
    loading.value = false
  }
}

const fetchMembers = async () => {
  try {
    loadingMembers.value = true
    const response = await api.get(`/committees/${route.params.id}/members`)
    members.value = response.data.members || []
  } catch (error) {
    console.error('Error fetching members:', error)
    toast.error('Failed to load committee members')
  } finally {
    loadingMembers.value = false
  }
}

const fetchBoardMembers = async () => {
  try {
    const response = await api.get('/members')
    boardMembers.value = response.data.members || []
  } catch (error) {
    console.error('Error fetching board members:', error)
  }
}

const openAddMemberDialog = () => {
  memberForm.value = {
    member_id: null,
    role: 'Member'
  }
  addMemberDialog.value = true
}

const addMember = async () => {
  if (!memberFormValid.value) return

  try {
    savingMember.value = true
    await api.post(`/committees/${route.params.id}/members`, memberForm.value)
    toast.success('Member added successfully')
    addMemberDialog.value = false
    await fetchMembers()
  } catch (error) {
    console.error('Error adding member:', error)
    toast.error(error.response?.data?.message || 'Failed to add member')
  } finally {
    savingMember.value = false
  }
}

const editMemberRole = (member) => {
  // TODO: Implement edit member role
  toast.info('Edit member role feature coming soon')
}

const removeMember = async (member) => {
  if (!confirm(`Remove ${member.name} from this committee?`)) return

  try {
    await api.delete(`/committees/${route.params.id}/members/${member.id}`)
    toast.success('Member removed successfully')
    await fetchMembers()
  } catch (error) {
    console.error('Error removing member:', error)
    toast.error(error.response?.data?.message || 'Failed to remove member')
  }
}

const editCommittee = () => {
  editForm.value = {
    name: committee.value.name,
    description: committee.value.description || '',
    chair_id: committee.value.chair_id,
    is_active: committee.value.is_active
  }
  editDialog.value = true
}

const closeEditDialog = () => {
  editDialog.value = false
  editFormRef.value?.reset()
}

const saveCommittee = async () => {
  if (!editFormValid.value) return

  try {
    savingEdit.value = true
    await api.put(`/committees/${route.params.id}`, editForm.value)
    toast.success('Committee updated successfully')
    closeEditDialog()
    await fetchCommittee()
  } catch (error) {
    console.error('Error updating committee:', error)
    toast.error(error.response?.data?.message || 'Failed to update committee')
  } finally {
    savingEdit.value = false
  }
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Lifecycle
onMounted(() => {
  fetchCommittee()
  fetchMembers()
  fetchBoardMembers()
})
</script>

<style scoped>
.v-avatar {
  flex-shrink: 0;
}
</style>
